import React from 'react';
import { Heart, Users, HandHeart } from 'lucide-react';

const About = () => {
    // Design Tokens / Constants for consistency
    const THEME = {
        primary: "#008080", // CP Teal
        secondary: "#D99C3B", // Gold/Orange
        bgLight: "#F0F7FA", // Light Blue/Teal Tint
        textGray: "text-gray-600",
        headingFont: "font-serif"
    };

    const ValueCard = ({ icon: Icon, title, description }) => (
        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center h-full">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 text-white bg-[${THEME.primary}] shadow-md`}>
                <Icon size={32} />
            </div>
            <h3 className={`text-xl font-bold text-[${THEME.secondary}] mb-4 ${THEME.headingFont}`}>{title}</h3>
            <p className={`${THEME.textGray} text-sm leading-relaxed`}>{description}</p>
        </div>
    );

    return (
        <React.Fragment>
            {/* 1. Introduction Section */}
            <section className="py-20 bg-white" id="about-intro">
                <div className="container mx-auto px-4 max-w-4xl text-center">
                    <h2 className={`text-[${THEME.secondary}] ${THEME.headingFont} text-5xl font-bold mb-4`}>About Us</h2>
                    <h3 className="text-2xl text-gray-700 font-light mb-8">Your Partner in Independence, Inclusion, and Community</h3>
                    <p className={`${THEME.textGray} text-lg leading-relaxed max-w-3xl mx-auto`}>
                        Community Plus Care Services is a registered NDIS provider dedicated to delivering high-quality, person-centered supports. We strive to create an inclusive environment where every individual is empowered to live with dignity and achieve their goals.
                    </p>
                </div>
            </section>

            {/* 2. Our Mission Section (Left Image, Right Text) */}
            <section className={`py-20 bg-[${THEME.bgLight}]`}>
                <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-12 lg:gap-20">
                    <div className="w-full md:w-1/2">
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl transition-transform duration-500 hover:scale-105">
                            <img
                                src="/assets/young-adult-helping-disabled-friend.jpg"
                                alt="Our Mission - Empowering Independence"
                                className="w-full h-[400px] object-cover"
                            />
                        </div>
                    </div>
                    <div className="w-full md:w-1/2">
                        <h2 className={`text-[${THEME.secondary}] ${THEME.headingFont} text-4xl lg:text-5xl font-bold mb-6`}>Our Mission</h2>
                        <div className="w-20 h-1 bg-[#008080] mb-8 rounded-full"></div>
                        <p className={`${THEME.textGray} text-lg leading-relaxed mb-6`}>
                            Our mission is to empower people with disability to live with independence, dignity, and confidence. We provide compassionate care that is tailored to your unique needs, helping you navigate daily life and connect with your community.
                        </p>
                        <p className={`${THEME.textGray} text-lg leading-relaxed`}>
                            We believe that with the right support, everyone can lead a fulfilling life. We are here to be the bridge to your opportunities.
                        </p>
                    </div>
                </div>
            </section>

            {/* 3. Our Core Values Section */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className={`text-[${THEME.secondary}] ${THEME.headingFont} text-4xl lg:text-5xl font-bold mb-4`}>Our Core Values</h2>
                        <p className="text-gray-500 max-w-2xl mx-auto">The principles that guide every interaction and service we provide.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        <ValueCard
                            icon={Heart}
                            title="Compassion & Opportunity"
                            description="We lead with kindness and empathy, ensuring every individual feels valued. We create opportunities for growth and happiness."
                        />
                        <ValueCard
                            icon={Users}
                            title="Partnership & Understanding"
                            description="We work collaboratively with participants and their families, listening deeply to understand your goals and aspirations."
                        />
                        <ValueCard
                            icon={HandHeart}
                            title="Reliability & Support"
                            description="You can count on us. We deliver consistent, high-quality care that provides the stability and support you need to thrive."
                        />
                    </div>
                </div>
            </section>

            {/* 4. Our Vision Section (Left Text, Right Image) */}
            <section className={`py-20 bg-[${THEME.bgLight}]`}>
                <div className="container mx-auto px-4 flex flex-col-reverse md:flex-row items-center gap-12 lg:gap-20">
                    <div className="w-full md:w-1/2">
                        <h2 className={`text-[${THEME.secondary}] ${THEME.headingFont} text-4xl lg:text-5xl font-bold mb-6`}>Our Vision</h2>
                        <div className="w-20 h-1 bg-[#008080] mb-8 rounded-full"></div>
                        <p className={`${THEME.textGray} text-lg leading-relaxed mb-6`}>
                            We envision a connected and inclusive community where everyone, regardless of ability, is supported to reach their full potential.
                        </p>
                        <p className={`${THEME.textGray} text-lg leading-relaxed`}>
                            By breaking down barriers and fostering understanding, we aim to build a society where support is seamless and every person is celebrated for who they are.
                        </p>
                    </div>
                    <div className="w-full md:w-1/2">
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl transition-transform duration-500 hover:scale-105">
                            <img
                                src="/assets/friends-having-fun-together.jpg"
                                alt="Our Vision - Inclusive Community"
                                className="w-full h-[400px] object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </React.Fragment>
    );
};

export default About;
